import React, { useEffect, useState } from "react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import AdminPortfolioPanel from "@/admin/AdminPortfolioPanel";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function extractErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function LoginView() {
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState({ status: "idle", error: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", error: "" });
    try {
      await login(email, password);
    } catch (err) {
      const detail = extractErrorDetail(err?.response?.data?.detail) || "Login failed. Please retry.";
      setState({ status: "error", error: detail });
    }
  };

  return (
    <div className="min-h-screen bg-white grain flex items-center justify-center px-6" data-testid="admin-login-view">
      <div className="w-full max-w-md">
        <div className="az-tag mb-8"><span className="az-tag__dot" /><span>AZ · Admin</span></div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tighter leading-[0.98] mb-4">
          Studio<span className="text-[#0055FF]">.</span>
        </h1>
        <p className="font-sans text-[15px] text-[#86868B] mb-12 max-w-sm">
          Accesso riservato. Inserisci le credenziali dello studio per consultare le richieste in arrivo.
        </p>

        <form onSubmit={onSubmit} className="space-y-8" data-testid="admin-login-form">
          <div>
            <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">Email</label>
            <input
              type="email"
              required
              autoFocus
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="az-input"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">Password</label>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="az-input"
              data-testid="admin-login-password"
            />
          </div>

          {state.status === "error" && (
            <div data-testid="admin-login-error" className="font-mono text-[11px] tracking-widest uppercase text-red-600">
              ✕ {state.error}
            </div>
          )}

          <button
            type="submit"
            disabled={state.status === "loading"}
            data-testid="admin-login-submit"
            className="az-btn"
          >
            <span>{state.status === "loading" ? "Accesso…" : "Accedi"}</span>
            <span className="az-btn__arrow" aria-hidden="true">→</span>
          </button>
        </form>

        <div className="mt-16 h-px w-full bg-[#E5E5EA]" />
        <div className="mt-6 font-mono text-[10px] tracking-widest uppercase text-[#86868B]">
          AZ Mechanical Design Studio · Area riservata
        </div>
      </div>
    </div>
  );
}

function DashboardView() {
  const { authFetch, logout, email } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("submissions");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await authFetch("/admin/submissions?limit=500");
      setItems(data);
    } catch (err) {
      const detail = extractErrorDetail(err?.response?.data?.detail) || "Errore nel caricamento";
      setError(detail);
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa richiesta?")) return;
    try {
      await authFetch(`/admin/submissions/${id}`, { method: "delete" });
      setItems((xs) => xs.filter((x) => x.id !== id));
      if (active?.id === id) setActive(null);
    } catch (err) {
      const detail = extractErrorDetail(err?.response?.data?.detail) || "Errore eliminazione";
      window.alert(detail);
    }
  };

  const downloadFile = async (row) => {
    try {
      const res = await authFetch(`/admin/submissions/${row.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = row.file_name || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      const detail = extractErrorDetail(err?.response?.data?.detail) || "Errore download file";
      window.alert(detail);
    }
  };

  const exportCsv = () => {
    const cols = ["created_at", "name", "company", "email", "language", "file_name", "project_description"];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(",")]
      .concat(items.map((r) => cols.map((c) => escape(r[c])).join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `az-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-white grain" data-testid="admin-dashboard-view">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#E5E5EA]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="az-tag"><span className="az-tag__dot" /><span>AZ · Admin</span></div>
            <span className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] hidden md:inline">{email}</span>
          </div>
          <div className="flex items-center gap-3">
            {tab === "submissions" && (
              <>
                <button data-testid="admin-refresh" onClick={load} className="az-link font-mono text-[11px] tracking-widest uppercase hidden md:inline">
                  Aggiorna
                </button>
                <button data-testid="admin-export-csv" onClick={exportCsv} className="az-link font-mono text-[11px] tracking-widest uppercase hidden md:inline">
                  Esporta CSV
                </button>
              </>
            )}
            <button data-testid="admin-logout" onClick={logout} className="az-btn az-btn--ghost text-[12px]">
              <span>Esci</span>
              <span className="az-btn__arrow" aria-hidden="true">→</span>
            </button>
          </div>
        </div>
        <nav className="mx-auto max-w-[1440px] px-6 md:px-10 flex items-center gap-6 border-t border-[#E5E5EA]">
          <button
            data-testid="admin-tab-submissions"
            onClick={() => setTab("submissions")}
            className={`py-3 font-mono text-[11px] tracking-widest uppercase transition-colors ${tab === "submissions" ? "text-[#0A0A0A] border-b border-[#0055FF]" : "text-[#86868B]"}`}
          >
            Richieste
          </button>
          <button
            data-testid="admin-tab-portfolio"
            onClick={() => setTab("portfolio")}
            className={`py-3 font-mono text-[11px] tracking-widest uppercase transition-colors ${tab === "portfolio" ? "text-[#0A0A0A] border-b border-[#0055FF]" : "text-[#86868B]"}`}
          >
            Portfolio
          </button>
        </nav>
      </header>

      {tab === "portfolio" ? (
        <AdminPortfolioPanel />
      ) : (
        <>
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 pt-16 md:pt-20 pb-12">
        <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.94]">
          Richieste ricevute<span className="text-[#0055FF]">.</span>
        </h1>
        <div className="mt-4 font-mono text-[11px] tracking-widest uppercase text-[#86868B]">
          {loading ? "Caricamento…" : `${items.length.toString().padStart(2, "0")} · richieste totali`}
        </div>
        {error && (
          <div data-testid="admin-error" className="mt-6 font-mono text-[11px] tracking-widest uppercase text-red-600">
            ✕ {error}
          </div>
        )}
      </section>

      <section className="mx-auto max-w-[1440px] px-6 md:px-10 pb-24">
        <div className="border-t border-[#E5E5EA]">
          <div className="hidden md:grid grid-cols-12 gap-4 py-3 font-mono text-[10px] tracking-widest uppercase text-[#86868B] border-b border-[#E5E5EA]">
            <div className="col-span-2">Data</div>
            <div className="col-span-2">Nome</div>
            <div className="col-span-2">Azienda</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">File</div>
            <div className="col-span-1 text-right">Azioni</div>
          </div>

          {!loading && items.length === 0 && (
            <div data-testid="admin-empty" className="py-16 text-center font-mono text-[11px] tracking-widest uppercase text-[#86868B]">
              Nessuna richiesta ricevuta.
            </div>
          )}

          {items.map((row) => (
            <div
              key={row.id}
              data-testid={`admin-row-${row.id}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 py-5 md:py-6 border-b border-[#E5E5EA] items-baseline hover:bg-[#F5F5F7] transition-colors duration-300"
            >
              <div className="col-span-2 font-mono text-[11px] tracking-widest uppercase text-[#86868B]">
                {formatDate(row.created_at)}
              </div>
              <div className="col-span-2 font-serif text-lg tracking-tight">{row.name}</div>
              <div className="col-span-2 font-sans text-sm text-[#0A0A0A]">{row.company || "—"}</div>
              <div className="col-span-3 font-sans text-sm">
                <a href={`mailto:${row.email}`} className="az-link">{row.email}</a>
              </div>
              <div className="col-span-2 font-sans text-sm text-[#86868B] truncate">
                {row.file_id ? (
                  <button
                    data-testid={`admin-download-${row.id}`}
                    onClick={() => downloadFile(row)}
                    className="font-mono text-[10px] tracking-widest uppercase text-[#0055FF] hover:text-[#0033CC]"
                    title={row.file_name}
                  >
                    ↓ {row.file_name || "file"}
                    {row.file_size ? ` · ${(row.file_size / (1024 * 1024)).toFixed(2)} MB` : ""}
                  </button>
                ) : (
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#86868B]/60">—</span>
                )}
              </div>
              <div className="col-span-1 flex md:justify-end gap-3">
                <button
                  data-testid={`admin-view-${row.id}`}
                  onClick={() => setActive(row)}
                  className="font-mono text-[10px] tracking-widest uppercase text-[#0055FF]"
                >
                  Apri
                </button>
                <button
                  data-testid={`admin-delete-${row.id}`}
                  onClick={() => onDelete(row.id)}
                  className="font-mono text-[10px] tracking-widest uppercase text-red-600"
                >
                  Elim.
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {active && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="admin-detail-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-white p-8 md:p-12 max-h-[85vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between mb-6">
              <div className="az-tag"><span className="az-tag__dot" /><span>Richiesta</span></div>
              <button onClick={() => setActive(null)} data-testid="admin-detail-close" className="font-mono text-[11px] tracking-widest uppercase">Chiudi ×</button>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tighter leading-[1.02]">{active.name}</h2>
            <div className="mt-2 font-mono text-[10px] tracking-widest uppercase text-[#86868B]">
              {formatDate(active.created_at)} · Lingua {active.language?.toUpperCase() || "IT"}
            </div>
            <div className="mt-8 space-y-6">
              <Row label="Azienda" value={active.company || "—"} />
              <Row label="Email" value={<a className="az-link" href={`mailto:${active.email}`}>{active.email}</a>} />
              <Row
                label="Allegato"
                value={
                  active.file_id ? (
                    <button
                      data-testid="admin-modal-download"
                      onClick={() => downloadFile(active)}
                      className="az-btn text-[12px]"
                    >
                      <span>
                        ↓ {active.file_name}
                        {active.file_size ? ` · ${(active.file_size / (1024 * 1024)).toFixed(2)} MB` : ""}
                      </span>
                    </button>
                  ) : "—"
                }
              />
              <div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-[#0055FF] mb-2">Descrizione</div>
                <p className="font-sans text-base leading-relaxed whitespace-pre-wrap">{active.project_description}</p>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] mb-1">{label}</div>
      <div className="font-sans text-base">{value}</div>
    </div>
  );
}

export default function AdminPage() {
  const { checking, isAuthenticated } = useAdminAuth();
  if (checking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="font-mono text-[11px] tracking-widest uppercase text-[#86868B]">Verifica sessione…</div>
      </div>
    );
  }
  return isAuthenticated ? <DashboardView /> : <LoginView />;
}
